import React, {ChangeEventHandler, FocusEventHandler, KeyboardEventHandler, useCallback, useEffect, useMemo, useState} from "react";
import _ from "lodash";
import {CFValue} from 'module/CustomField/type/CFType'
import {log} from 'common/log'
import {CFComponentProps} from 'module/CustomField/type/CFComponentType'
import {stripHTML} from 'util/StringUtil'
import {cssCFInput} from 'module/CustomField/style/cssCFInput'
import {css} from '@emotion/react'
import {toFloat} from 'module/CustomField/util/CFDefUIUtil'
import {CF} from 'module/CustomField/util/CustomField'
import {CFAttrKey} from 'module/CustomField/constant/CFAttrConstants'

interface CustomFieldNumberComponentProps extends CFComponentProps<CFValue> {
	precision?: number
	round?: number
	step?: number
}

const cssNumberInput = css`
	text-align: right;
`

interface CalculateProps {
	value: CFValue;
	step?: number;
	precision?: number;
	min?: number;
	max?: number;
}

function increment({value, step = 1, precision, max}: CalculateProps) {
	let v: number = Number(value) || 0;
	v += step;
	if (max && v > max) v = max;
	return toFloat(v, precision) + '';
}

function decrement({value, step = 1, precision, min}: CalculateProps) {
	let v: number = Number(value) || 0;
	v -= step;
	if ((min || min == 0) && v < min) v = min;
	return toFloat(v, precision) + '';
}

function adaptValuePrecision(value: CFValue, precision?: number) {
	if (value == null) return '';
	if (isNaN(+value)) return value + '';
	if (!precision) return toFloat(value);
	return toFloat(value, precision);
}

export function CustomFieldNumberComponent({modelValue, disabled, placeholderHTML, setCb, cfDef}: CustomFieldNumberComponentProps) {
	const {step, precision, min, max} = useMemo(() => ({
		step: Number(CF.getCFAttr(cfDef, CFAttrKey.step)?.value),
		precision: Number(CF.getCFAttr(cfDef, CFAttrKey.precision)?.value),
		min: Number(CF.getCFAttr(cfDef, CFAttrKey.min)?.value),
		max: Number(CF.getCFAttr(cfDef, CFAttrKey.max)?.value),
	}), [cfDef])

	const [viewValue, setViewValue] = useState<CFValue>(adaptValuePrecision(modelValue, precision));

	useEffect(() => {
		setViewValue(adaptValuePrecision(modelValue, precision));
	}, [modelValue, precision])

	//precision used in type=F parser
	const onKeyDown = useCallback<KeyboardEventHandler<HTMLInputElement>>((event) => {
		if (event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) return;
		switch (event.code) {
			case 'ArrowUp':
				setViewValue(increment({value: viewValue, step, precision, max}))
				break;
			case 'ArrowDown':
				setViewValue(decrement({value: viewValue, step, precision, min}));
				break;
			case 'Escape':
				setViewValue(adaptValuePrecision(modelValue, precision))
				break;
			default:
				log.debug("Key: '" + event.code + "' pressed. No action defined");
		}
	}, [modelValue, viewValue, cfDef, step, min, max, precision]);

	const onChange: ChangeEventHandler<HTMLInputElement> = useCallback((evt) => {
		let v = _.replace(evt.target.value, ',', '.');
		setViewValue(v);
	}, [])

	const onFocus: FocusEventHandler<HTMLInputElement> = useCallback((evt) => evt.target.select(), [])

	const onBlur = useCallback(() => {
		setViewValue(adaptValuePrecision(modelValue, precision));
		if (!disabled) {
			setCb(viewValue);
		}
	}, [modelValue, precision, disabled, setCb, viewValue])

	return (<div className={'CustomFieldNumber'}>
		<input
			type={'text'}
			data-cursor-key-needs-modifier={true}
			css={[cssCFInput, cssNumberInput]}
			value={viewValue || ''}
			onChange={onChange}
			onFocus={onFocus}
			onBlur={onBlur}
			disabled={disabled}
			placeholder={stripHTML(placeholderHTML)}
			onKeyDown={onKeyDown}
		/>
	</div>)
}